'use client';

import { useState, useEffect } from 'react';
import { Button, Label } from 'flowbite-react';
import { EyeOff, ChevronLeft } from 'lucide-react';
import bgLatto from '../../assets/images/bg-latto.jpg';
import Logo from '../../assets/images/logo.webp';
import EyeOpen from '../../assets/icons/Eye.svg?react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import TextInput from '../../components/ui/TextInput';
import { authApi } from '../../api/auth';
import Swal from 'sweetalert2';

export default function RegisterPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [fullName, setFullName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [countryCode, setCountryCode] = useState('63');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Extract the code query parameter from the URL
    const queryParams = new URLSearchParams(location.search);
    const codeFromUrl = queryParams.get('code');

    // If code exists in URL, set it to the referralCode state
    if (codeFromUrl) {
      setReferralCode(codeFromUrl);
    }
  }, [location.search]);

  // Check password strength whenever password changes
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    // Length check
    if (password.length >= 8) strength += 1;
    // Contains number
    if (/\d/.test(password)) strength += 1;
    // Contains lowercase
    if (/[a-z]/.test(password)) strength += 1;
    // Contains uppercase
    if (/[A-Z]/.test(password)) strength += 1;
    // Contains special char
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;

    setPasswordStrength(strength);
  }, [password]);

  // Format mobile number as user types
  const handleMobileNumberChange = (e) => {
    const value = e.target.value;
    // Only allow digits
    const digits = value.replace(/\D/g, '');
    // Limit to 11 digits
    const formattedNumber = digits.slice(0, 11);
    if (formattedNumber.startsWith('0')) {
      setMobileNumber(formattedNumber.slice(1));
    } else {
      setMobileNumber(formattedNumber);
    }
  };

  const handleCountryCodeChange = (e) => {
    setCountryCode(e.target.value);
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!fullName.trim()) {
      newErrors.fullName = 'Full Name is required';
      isValid = false;
    }

    // Mobile number validation
    if (!mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile Number is required';
      isValid = false;
    } else {
      // Remove any non-digit characters
      const digits = mobileNumber.replace(/\D/g, '');

      // countryCode holds '63' (no plus), so this check never ran before.
      if (countryCode === '63' || countryCode === '+63') {
        // PH: 10 digits starting with 9 (the backend requires 639XXXXXXXXX)
        if (digits.length !== 10 || !digits.startsWith('9')) {
          newErrors.mobileNumber = 'PH mobile number must be 10 digits starting with 9 (e.g., 9123456789)';
          isValid = false;
        }
      } else if (countryCode === '+1' || countryCode === '+44' || countryCode === '+91') {
        // US/UK/IN: must be 10 digits
        if (digits.length !== 10) {
          newErrors.mobileNumber = 'Mobile number must be 10 digits';
          isValid = false;
        }
      } else {
        // Default: 8-12 digits
        if (digits.length < 8 || digits.length > 12) {
          newErrors.mobileNumber = 'Mobile number must be 8-12 digits';
          isValid = false;
        }
      }
    }

    if (!password.trim()) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      isValid = false;
    } else if (passwordStrength < 3) {
      newErrors.password = 'Password is too weak';
      isValid = false;
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
      isValid = false;
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    // if (!referralCode.trim()) {
    //   newErrors.referralCode = 'Referral Code is required';
    //   isValid = false;
    // }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
  
    if (!validateForm()) return;
  
    setIsLoading(true);
  
    try {
      const userData = {
        name: fullName,
        mobileNumber: countryCode + mobileNumber,
        password,
        referralCode,
        // Do NOT include otpAccessToken
      };
      const response = await authApi.register(userData);
  
      Swal.fire({
        title: 'Registration Successful!',
        text: 'Your account has been created successfully.',
        icon: 'success',
        confirmButtonText: 'Continue to Login',
        confirmButtonColor: '#1e3a8a',
      }).then(() => {
        navigate('/login', {
          state: {
            message: 'Registration successful! Please login to continue.',
          },
        });
      });
    } catch (err) {
      Swal.fire({
        title: 'Registration Error',
        text: err.message || 'Registration failed. Please try again.',
        icon: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitWithOtp = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) return;

    setIsLoading(true);

    // Prepare the mobile number in the format required by MSG91
    const identifier = `+${countryCode}${mobileNumber}`;

    // Load the MSG91 script and trigger the widget
    loadMsg91Script(() => {
      const configuration = {
        widgetId: "356443616542323034303537", // your widgetId
        tokenAuth: "446301Tm0eimxmULD6810264fP1", // your tokenAuth
        identifier,
        exposeMethods: false,
        success: async (data) => {
          try {
            const userData = {
              name: fullName,
              mobileNumber: countryCode + mobileNumber,
              password,
              referralCode,
              otpAccessToken: data.message, // or data.token if exposeMethods: true
            };
            const response = await authApi.register(userData);

            // If response is not ok, show error and return early
            if (!response || response.ok === false || response.status === 401) {
              Swal.fire({
                title: 'Registration Error',
                text: response?.message || 'Registration failed. Please try again.',
                icon: 'error',
              });
              return; // Do not proceed to login
            }

            // If response has a .status property and it's not 201, treat as error
            if (response.status && response.status !== 201) {
              Swal.fire({
                title: 'Registration Error',
                text: response?.message || 'Registration failed. Please try again.',
                icon: 'error',
              });
              return;
            }

            // Registration successful
            Swal.fire({
              title: 'Registration Successful!',
              text: 'Your account has been created successfully.',
              icon: 'success',
              confirmButtonText: 'Continue to Login',
              confirmButtonColor: '#1e3a8a',
            }).then(() => {
              navigate('/login', {
                state: {
                  message: 'Registration successful! Please login to continue.',
                },
              });
            });
          } catch (err) {
            Swal.fire({
              title: 'Registration Error',
              text: err.message || 'Registration failed. Please try again.',
              icon: 'error',
            });
          } finally {
            setIsLoading(false);
          }
        },
        failure: (error) => {
          setIsLoading(false);
          Swal.fire({
            title: 'OTP Verification Failed',
            text: error.message || 'OTP verification failed. Please try again.',
            icon: 'error',
          });
        },
      };

      // Initialize the widget
      window.initSendOTP(configuration);
    });

  };

  // Render the password strength indicator
  const renderPasswordStrength = () => {
    if (!password) return null;

    const strengthLabels = [
      'Very Weak',
      'Weak',
      'Medium',
      'Strong',
      'Very Strong',
    ];
    const strengthColors = [
      'bg-red-500',
      'bg-orange-500',
      'bg-yellow-500',
      'bg-blue-500',
      'bg-green-500',
    ];

    return (
      <div className='mt-1'>
        <div className='flex gap-1 h-1 mb-1'>
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className={`h-full flex-1 rounded-sm ${
                index < passwordStrength ? strengthColors[index] : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <p className='text-xs text-gray-600'>
          Password strength:{' '}
          {strengthLabels[passwordStrength - 1] || 'Very Weak'}
        </p>
      </div>
    );
  };

  // Utility to load the MSG91 OTP widget script only once
  function loadMsg91Script(onLoad) {
    if (document.getElementById('msg91-otp-script')) {
      onLoad();
      waitAndHideOtpFooter();
      return;
    }
    const script = document.createElement('script');
    script.id = 'msg91-otp-script';
    script.src = 'https://verify.msg91.com/otp-provider.js';
    script.type = 'text/javascript';
    script.onload = () => {
      onLoad();
      waitAndHideOtpFooter();
    };
    document.body.appendChild(script);
  }

  function hideOtpFooter() {
    // Find the <msg91-otp-provider> element
    const otpProvider = document.querySelector('msg91-otp-provider');
    if (otpProvider && otpProvider.shadowRoot) {
      // Query inside the shadow root
      const footer = otpProvider.shadowRoot.querySelector('.otp-verification-footer');
      if (footer) {
        footer.style.display = 'none';
        return true;
      }
    }
    return false;
  }

  function waitAndHideOtpFooter() {
    const interval = setInterval(() => {
      if (hideOtpFooter()) {
        clearInterval(interval);
      }
    }, 300); // check every 100ms
    // Optionally, add a timeout to stop after a few seconds
    setTimeout(() => clearInterval(interval), 5000);
  }

  return (
    <div className='min-h-screen relative overflow-hidden flex flex-col bg-[#1e3a8a]'>
      {/* Background Balls */}
      <div className='absolute inset-0 z-0'>
        <img
          src={bgLatto}
          alt='Background'
          className='w-full h-full object-cover'
        />
      </div>

      {/* Logo Section */}
      <div className='relative z-10 flex-grow flex flex-col items-center justify-center p-4'>
        <img
          src={Logo}
          className='mb-2 w-[149px] h-[149px] max-xs:w-[120px] max-xs:h-[120px]'
          aria-label='E-LOTTO Logo'
        />
      </div>

      {/* Content Section */}
      <div className='relative z-10 bg-white rounded-t-[40px] p-6 w-full'>
        <div className='flex items-center my-5 mb-10 max-xs:my-3 max-xs:mb-5'>
          {/* Back Button */}
          <button
            className='text-gray-600'
            onClick={() => navigate(-1)}
            aria-label='Go back'
          >
            <ChevronLeft className='h-6 w-6' />
          </button>

          {/* Form Title */}
          <h1 className='text-gray-800 text-2xl font-bold font-poppins flex-grow text-center'>
            Register
          </h1>
        </div>

        {/* Registration Form */}
        <form
          className='space-y-5 max-xs:space-y-3 px-6 my-5'
          onSubmit={handleSubmit}
          noValidate
        >
          <div>
            <Label
              htmlFor='fullName'
              className='mb-1 block text-sm font-medium text-gray-700'
            >
              Full Name
            </Label>
            <TextInput
              id='fullName'
              type='text'
              placeholder='Enter your full name'
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={`w-full rounded-md p-2 ${
                errors.fullName ? 'border-red-500' : 'border-gray-300'
              }`}
              aria-invalid={errors.fullName ? 'true' : 'false'}
              aria-describedby={errors.fullName ? 'fullName-error' : undefined}
            />
            {errors.fullName && (
              <p id='fullName-error' className='mt-1 text-sm text-red-600'>
                {errors.fullName}
              </p>
            )}
          </div>
          
          <div>
            <Label
              htmlFor='mobileNumber'
              className='mb-1 block text-sm font-medium text-gray-700'
            >
              Mobile Number
            </Label>
            <div className='flex gap-1'>
              <select
                value={countryCode}
                onChange={handleCountryCodeChange}
                className='rounded-md border border-gray-300 p-2 bg-gray-50 text-sm focus:ring-blue-500 focus:border-blue-500'
                aria-label='Country code'
              >
                <option value='63'>+63</option>
                {/* <option value='63'>🇵🇭 +63</option>
                <option value='1'>🇺🇸 +1</option>
                <option value='44'>🇬🇧 +44</option>
                <option value='91'>🇮🇳 +91</option>
                <option value='65'>🇸🇬 +65</option>
                <option value='62'>🇮🇩 +62</option>
                <option value='60'>🇲🇾 +60</option> */}
              </select>
              <TextInput
                containerClassName='w-full'
                autoComplete='off'
                id='mobileNumber'
                type='tel'
                placeholder='Enter 10-11 digit mobile number'
                required
                value={mobileNumber}
                onChange={handleMobileNumberChange}
                className={`w-full rounded-md p-2 ${
                  errors.mobileNumber ? 'border-red-500' : 'border-gray-300'
                }`}
                aria-invalid={errors.mobileNumber ? 'true' : 'false'}
                aria-describedby={
                  errors.mobileNumber ? 'mobileNumber-error' : undefined
                }
              />
            </div>
            {errors.mobileNumber && (
              <p id='mobileNumber-error' className='mt-1 text-sm text-red-600'>
                {errors.mobileNumber}
              </p>
            )}
          </div>

          <div>
            <Label
              htmlFor='password'
              className='mb-1 block text-sm font-medium text-gray-700'
            >
              Password
            </Label>
            <div className='relative'>
              <TextInput
                id='password'
                type={showPassword ? 'text' : 'password'}
                placeholder='Create a strong password'
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full rounded-md p-2 ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
                aria-invalid={errors.password ? 'true' : 'false'}
                aria-describedby={
                  errors.password
                    ? 'password-error password-requirements'
                    : 'password-requirements'
                }
              />
              <button
                type='button'
                className='absolute right-3 top-1/2 -translate-y-1/2'
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className='h-5 w-5 text-gray-500' />
                ) : (
                  <EyeOpen className='w-5 h-5 object-cover' />
                )}
              </button>
            </div>
            {renderPasswordStrength()}
            <p
              id='password-requirements'
              className='mt-1 text-xs text-gray-600'
            >
              Password must be at least 8 characters and include uppercase,
              lowercase, number and special character.
            </p>
            {errors.password && (
              <p id='password-error' className='mt-1 text-sm text-red-600'>
                {errors.password}
              </p>
            )}
          </div>

          <div>
            <Label
              htmlFor='confirmPassword'
              className='mb-1 block text-sm font-medium text-gray-700'
            >
              Confirm Password
            </Label>
            <div className='relative'>
              <TextInput
                id='confirmPassword'
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder='Confirm your password'
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full rounded-md p-2 ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                }`}
                aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                aria-describedby={
                  errors.confirmPassword ? 'confirmPassword-error' : undefined
                }
              />
              <button
                type='button'
                className='absolute right-3 top-1/2 -translate-y-1/2'
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={
                  showConfirmPassword ? 'Hide password' : 'Show password'
                }
              >
                {showConfirmPassword ? (
                  <EyeOff className='h-5 w-5 text-gray-500' />
                ) : (
                  <EyeOpen className='w-5 h-5 object-cover' />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p
                id='confirmPassword-error'
                className='mt-1 text-sm text-red-600'
              >
                {errors.confirmPassword}
              </p>
            )}
          </div>

          {referralCode && (
            <div>
              <Label
                htmlFor='referralCode'
              className='mb-1 block text-sm font-medium text-gray-700'
            >
              Referral Code <span className='text-red-500'>*</span>
            </Label>
            <TextInput
              id='referralCode'
              type='text'
              placeholder='Enter referral code'
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              className={`w-full rounded-md p-2 ${
                errors.referralCode ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              aria-invalid={errors.referralCode ? 'true' : 'false'}
              aria-describedby={
                errors.referralCode ? 'referralCode-error' : undefined
              }
            />
            {errors.referralCode && (
              <p id='referralCode-error' className='mt-1 text-sm text-red-600'>
                {errors.referralCode}
              </p>
              )}
            </div>
          )}

          <div className='text-[9.5px] text-gray-600'>
            By login or sign up, you are confirming that you are above 18 years
            old, and agreed to our{' '}
            <Link to='/terms' className='text-blue-600 hover:underline'>
              Terms and Conditions
            </Link>
          </div>

          <Button
            type='submit'
            className='w-full bg-blue-800 hover:bg-blue-900 text-white text-base font-medium py-2 max-xs:py-1 rounded-md'
            disabled={isLoading}
          >
            {isLoading ? (
              <div className='flex items-center justify-center'>
                <div className='h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2'></div>
                <span>Registering...</span>
              </div>
            ) : (
              'Register'
            )}
          </Button>

          <div className='text-center text-base max-xs:text-sm text-black font-manrope font-bold'>
            Already have an account?{'  '}
            <Link to='/login' className='text-blue-600 hover:underline'>
              Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
